/** Limite conservador para JSON de sync (fotos pequenas em base64). Vídeos usam upload multipart. */
const MAX_PUSH_JSON_BYTES = 32 * 1024 * 1024;

export type SlimPushPayloadResult = {
  payload: Record<string, unknown>;
  strippedPhotoCount: number;
  approxBytes: number;
};

/**
 * Clone para fila/sync. structuredClone falha com proxies Vue e alguns tipos de mídia no APK.
 */
function clonePushPayload(payload: Record<string, unknown>): Record<string, unknown> {
  try {
    return JSON.parse(
      JSON.stringify(payload, (_key, value) => {
        if (typeof value === 'function' || typeof value === 'symbol') {
          return undefined;
        }

        if (typeof File !== 'undefined' && value instanceof File) {
          return undefined;
        }

        if (typeof Blob !== 'undefined' && value instanceof Blob) {
          return undefined;
        }

        if (typeof value === 'bigint') {
          return value.toString();
        }

        return value;
      }),
    ) as Record<string, unknown>;
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);

    throw new Error(`Não foi possível preparar o RDO para envio: ${detail}`);
  }
}

function stripInlineEvidenceDataUrl(clone: Record<string, unknown>): boolean {
  if (!Array.isArray(clone.evidencias)) {
    return false;
  }

  for (const entry of clone.evidencias) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }

    const evidence = entry as Record<string, unknown>;
    const dataUrl = evidence.dataUrl;
    const storagePath = evidence.storagePath;
    const localPath = evidence.localPath;
    const mimeType = String(evidence.mimeType ?? '');

    if (typeof localPath === 'string' && localPath !== '') {
      delete evidence.dataUrl;
      return true;
    }

    if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
      continue;
    }

    if (typeof storagePath === 'string' && storagePath !== '') {
      delete evidence.dataUrl;
      return true;
    }

    if (mimeType.startsWith('video/')) {
      delete evidence.dataUrl;
      return true;
    }
  }

  const index = clone.evidencias.findIndex(
    (entry) =>
      entry &&
      typeof entry === 'object' &&
      typeof (entry as Record<string, unknown>).dataUrl === 'string' &&
      ((entry as Record<string, unknown>).dataUrl as string).startsWith('data:'),
  );

  if (index < 0) {
    return false;
  }

  delete (clone.evidencias[index] as Record<string, unknown>).dataUrl;

  return true;
}

/**
 * Reduz evidências base64 no push para evitar 413/422 quando o JSON estoura o limite do PHP.
 */
export function slimFieldReportPushPayload(
  payload: Record<string, unknown>,
): SlimPushPayloadResult {
  const clone = clonePushPayload(payload);
  let strippedPhotoCount = 0;

  if (Array.isArray(clone.evidencias)) {
    for (const entry of clone.evidencias) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }

      const evidence = entry as Record<string, unknown>;
      if (
        typeof evidence.storagePath === 'string' &&
        evidence.storagePath !== '' &&
        typeof evidence.dataUrl === 'string' &&
        evidence.dataUrl.startsWith('data:')
      ) {
        delete evidence.dataUrl;
        strippedPhotoCount += 1;
      }
    }
  }

  let serialized = JSON.stringify(clone);
  let approxBytes = new TextEncoder().encode(serialized).length;

  if (approxBytes <= MAX_PUSH_JSON_BYTES) {
    return {
      payload: clone,
      strippedPhotoCount,
      approxBytes,
    };
  }

  while (approxBytes > MAX_PUSH_JSON_BYTES && stripInlineEvidenceDataUrl(clone)) {
    strippedPhotoCount += 1;
    serialized = JSON.stringify(clone);
    approxBytes = new TextEncoder().encode(serialized).length;
  }

  return {
    payload: clone,
    strippedPhotoCount,
    approxBytes,
  };
}
